import type { PureFunction } from '@bonsai-components/utility-types';
import type { LucideIcon } from 'lucide-react';
import {
  GLOBAL_ORGANIZATION_ID,
  isUserOrgGroupingNode
} from '../../helpers/organizations.helper';
import { capitalize } from '../../helpers/string.helper';
import { GlobalOrganizationIcon } from '../../icons/icons';
export type OrgRowActionTarget = {
  id: string;
  name: string;
  isEditable: boolean;
};

export type OrganizationRowData = {
  id: string;
  name: string;
  /**
   * Disabled for performance reasons
   */
  // repositoryCount: number;
  isUserOrganization: boolean;
  parent?: string;
  path: string[];
};

export type OrganizationTreeItem = {
  id: string;
  label: string;
  icon?: LucideIcon;
  isUserOrganization: boolean;
  isEditable: boolean;
  repositoryCount?: number;
  /**
   * Total direct children count from the API. Optional because not every
   * build path supplies it; both the searched and unsearched tree flows use
   * it to decide whether an item renders with a chevron (folder) or as a leaf.
   */
  childCount?: number;
  children?: OrganizationTreeItem[];
};

export type SearchHitAncestor = {
  id: string;
  label: string;
  childCount?: number;
};

export type SearchHit = {
  item: OrganizationTreeItem;
  ancestors: SearchHitAncestor[];
};

// ── Lazy spine tree (unsearched view) ───────────────────────────────────────

/**
 * Synthetic id suffix used for "Show more" rows injected as tree-item
 * children of folders that have hidden siblings. The unsearched
 * `RichTreeViewPro` consumes these alongside real children; the
 * `CustomTreeItem` slot recognizes the suffix and renders a click-only
 * row that fires the next-page fetch.
 */
const SHOW_MORE_ITEM_SUFFIX = '__more__';

const buildShowMoreItemId = (folderId: string): string =>
  `${folderId}${SHOW_MORE_ITEM_SUFFIX}`;

export const isShowMoreItemId = (id: string): boolean =>
  id.endsWith(SHOW_MORE_ITEM_SUFFIX);

export const showMoreItemFolderId = (id: string): string =>
  id.slice(0, id.length - SHOW_MORE_ITEM_SUFFIX.length);

/**
 * Invisible placeholder child injected to force `RichTreeViewPro` to
 * mark an item as expandable. MUI's items plugin sets
 * `isItemExpandable: (item, children) => !!children && children.length > 0`,
 * so an item with no `children` array is never rendered with a chevron
 * — even if our `CustomTreeItem` slot would draw one. By attaching a
 * single placeholder child to items that haven't been fetched yet but
 * the API reports as having children (`childCount > 0`) or repositories
 * (`repositoryCount > 0`), the chevron renders and clicking it fires
 * the on-demand fetch. The placeholder is replaced as soon as real
 * children arrive in `revealedChildren`.
 */
const PLACEHOLDER_ITEM_SUFFIX = '__placeholder__';

const buildPlaceholderItemId = (parentId: string): string =>
  `${parentId}${PLACEHOLDER_ITEM_SUFFIX}`;

export const isPlaceholderItemId = (id: string): boolean =>
  id.endsWith(PLACEHOLDER_ITEM_SUFFIX);

type LazyAncestor = { id: string; name: string };

type BuildLazySpineTreeInput = {
  /**
   * Per-folder first-page children, already converted to tree items.
   * Keyed by parent org id.
   */
  revealedChildren: Map<string, OrganizationTreeItem[]>;
  /** Folders the renderer should append a Show more row beneath. */
  foldersWithMore: Set<string>;
  /**
   * Selected org's ancestor chain, **root-most first, selected last**.
   * Empty when no org is selected or when the selected org is Global.
   * Used to splice spine descendants into their ancestor's children list
   * when the descendant isn't already in the fetched first page.
   */
  spine?: LazyAncestor[];
  /**
   * The "My organizations" org meta (id + name). When provided, it is
   * pinned as Global's first child so the user's own orgs are visible
   * without scrolling the global tree.
   */
  myOrgsRoot?: LazyAncestor;
};

/**
 * Builds the unsearched lazy tree from the fetched children pages.
 *
 * - Global is always the root, with its icon.
 * - For each spine ancestor (root-most first), if the spine descendant is
 *   not already in the ancestor's first page, the descendant is prepended
 *   so the path to the selected org is always visible.
 * - Folders in `foldersWithMore` get a synthetic Show more child appended.
 * - `myOrgsRoot` is pinned as Global's first child (deduped if already
 *   present in the page).
 */
export const buildLazySpineTree: PureFunction<
  BuildLazySpineTreeInput,
  OrganizationTreeItem[]
> = ({ revealedChildren, foldersWithMore, spine, myOrgsRoot }) => {
  const ancestorById = new Map<string, LazyAncestor>();
  if (spine) {
    for (const ancestor of spine) {
      ancestorById.set(ancestor.id, ancestor);
    }
  }

  const buildAncestorPlaceholder = (
    ancestor: LazyAncestor
  ): OrganizationTreeItem => ({
    id: ancestor.id,
    label: ancestor.id === GLOBAL_ORGANIZATION_ID ? 'Global' : ancestor.name,
    icon:
      ancestor.id === GLOBAL_ORGANIZATION_ID
        ? GlobalOrganizationIcon
        : undefined,
    isUserOrganization: false,
    isEditable: false
  });

  /**
   * Wraps a fetched child with a placeholder `children: [...]` array
   * when the API reports it has child orgs (`childCount > 0`) but we
   * haven't fetched them yet. Required for MUI's items plugin to render
   * the chevron — see `PLACEHOLDER_ITEM_SUFFIX` comment. Matches the
   * search flow's `childIsKnownFolder` rule in `OrgFilteredTree` — only
   * orgs with descendants get chevrons; repository-only leaves stay flat.
   */
  const withPlaceholderIfExpandable = (
    item: OrganizationTreeItem
  ): OrganizationTreeItem => {
    if (item.children && item.children.length > 0) {
      return item;
    }
    if ((item.childCount ?? 0) === 0) {
      return item;
    }
    return {
      ...item,
      children: [
        {
          id: buildPlaceholderItemId(item.id),
          label: '',
          isUserOrganization: false,
          isEditable: false
        }
      ]
    };
  };

  /**
   * Build the children array for a folder, splicing in spine descendants
   * and appending a Show more row when there are hidden siblings.
   */
  const buildChildren = (
    folderId: string,
    spineDescendantId: string | undefined
  ): OrganizationTreeItem[] => {
    const fetched = revealedChildren.get(folderId);
    const result: OrganizationTreeItem[] = [];

    // Pin My organizations under Global if we know about it.
    if (folderId === GLOBAL_ORGANIZATION_ID && myOrgsRoot) {
      const alreadyInPage = fetched?.some((c) => c.id === myOrgsRoot.id);
      if (!alreadyInPage) {
        result.push(buildAncestorPlaceholder(myOrgsRoot));
      }
    }

    // Pin spine descendant at the front if not already in the fetched page.
    if (spineDescendantId && spineDescendantId !== myOrgsRoot?.id) {
      const ancestor = ancestorById.get(spineDescendantId);
      const inPage = fetched?.some((c) => c.id === spineDescendantId);
      if (ancestor && !inPage) {
        result.push(buildAncestorPlaceholder(ancestor));
      }
    }

    if (fetched) {
      for (const child of fetched) {
        result.push(withPlaceholderIfExpandable(child));
      }
    }

    if (foldersWithMore.has(folderId)) {
      result.push({
        id: buildShowMoreItemId(folderId),
        label: '',
        isUserOrganization: false,
        isEditable: false
      });
    }

    return result;
  };

  /**
   * Recursively attach children to the spine. For each spine ancestor we
   * descend into its corresponding revealed children and find the next
   * spine node. Non-spine subtrees are populated from `revealedChildren`
   * on demand (i.e., when the user later expands them) — at build time
   * we only attach what's been fetched so far.
   */
  const attachSubtree = (
    item: OrganizationTreeItem,
    spineDescendantId: string | undefined
  ): void => {
    const children = buildChildren(item.id, spineDescendantId);
    if (children.length === 0) {
      return;
    }
    item.children = children;

    // Recurse into non-Show-more children that themselves have revealed
    // pages — covers the spine descendant and any user-expanded folder.
    for (const child of children) {
      if (isShowMoreItemId(child.id)) {
        continue;
      }
      if (!revealedChildren.has(child.id) && !ancestorById.has(child.id)) {
        continue;
      }
      const nextSpine =
        child.id === spineDescendantId
          ? findNextSpine(child.id, spine)
          : undefined;
      attachSubtree(child, nextSpine);
    }
  };

  const findNextSpine = (
    currentId: string,
    chain: LazyAncestor[] | undefined
  ): string | undefined => {
    if (!chain) {
      return undefined;
    }
    const idx = chain.findIndex((a) => a.id === currentId);
    if (idx === -1) {
      return undefined;
    }
    return chain[idx + 1]?.id;
  };

  const globalAncestor: LazyAncestor = spine?.find(
    (a) => a.id === GLOBAL_ORGANIZATION_ID
  ) ?? {
    id: GLOBAL_ORGANIZATION_ID,
    name: GLOBAL_ORGANIZATION_ID
  };
  const globalItem = buildAncestorPlaceholder(globalAncestor);
  const firstSpineDescendant = findNextSpine(GLOBAL_ORGANIZATION_ID, spine);
  attachSubtree(globalItem, firstSpineDescendant);

  return [globalItem];
};

/**
 * Returns the last visible item ID in the tree, considering which items are expanded.
 * Walks to the deepest last-child of expanded nodes.
 */
export const getLastVisibleItemId: PureFunction<
  { items: OrganizationTreeItem[]; expandedItems: string[] },
  string | undefined
> = ({ items, expandedItems }) => {
  const expandedSet = new Set(expandedItems);

  const findLast = (nodes: OrganizationTreeItem[]): string | undefined => {
    const lastNode = nodes.at(-1);
    if (!lastNode) {
      return undefined;
    }
    if (lastNode.children?.length && expandedSet.has(lastNode.id)) {
      return findLast(lastNode.children) ?? lastNode.id;
    }
    return lastNode.id;
  };

  return findLast(items);
};

/**
 * Builds a sparse "spine" tree from server-side search hits.
 *
 * Each hit has a `parents` chain (immediate parent first, ordered toward
 * root). This walks every chain, synthesizing an `OrganizationTreeItem`
 * for each ancestor and wiring the parent → child relationships. Ancestors
 * carry only `id` and `label`; matches keep their full data.
 *
 * Output is the array of root spine items (folders with no parent in the
 * sparse tree), suitable for `buildPrunedTree` to consume.
 */
export type SpineHitInput = {
  id: string;
  label: string;
  icon?: LucideIcon;
  isUserOrganization: boolean;
  isEditable: boolean;
  repositoryCount?: number;
  childCount?: number;
  /** Ancestor chain, immediate parent first, ordered toward root. */
  parents: SearchHitAncestor[];
};

export const buildSpineFromHits: PureFunction<
  { hits: SpineHitInput[] },
  OrganizationTreeItem[]
> = ({ hits }) => {
  const itemsById = new Map<string, OrganizationTreeItem>();
  const parentIdById = new Map<string, string | undefined>();

  for (const hit of hits) {
    if (!itemsById.has(hit.id)) {
      itemsById.set(hit.id, {
        id: hit.id,
        label: hit.label,
        icon:
          hit.icon ??
          (hit.id === GLOBAL_ORGANIZATION_ID
            ? GlobalOrganizationIcon
            : undefined),
        isUserOrganization: hit.isUserOrganization,
        isEditable: hit.isEditable,
        repositoryCount: hit.repositoryCount,
        childCount: hit.childCount
      });
    }
    parentIdById.set(hit.id, hit.parents[0]?.id);

    // parents[0] is immediate parent; parents[N-1] is root-most ancestor.
    for (let i = 0; i < hit.parents.length; i++) {
      const ancestor = hit.parents[i];
      if (!itemsById.has(ancestor.id)) {
        itemsById.set(ancestor.id, {
          id: ancestor.id,
          label: ancestor.label,
          isUserOrganization: false,
          isEditable: false,
          childCount: ancestor.childCount,
          icon:
            ancestor.id === GLOBAL_ORGANIZATION_ID
              ? GlobalOrganizationIcon
              : undefined
        });
      }
      const parentOfAncestor = hit.parents[i + 1]?.id;
      if (!parentIdById.has(ancestor.id)) {
        parentIdById.set(ancestor.id, parentOfAncestor);
      }
    }
  }

  // Wire children relationships
  for (const [childId, parentId] of parentIdById.entries()) {
    if (!parentId) {
      continue;
    }
    const parentItem = itemsById.get(parentId);
    const childItem = itemsById.get(childId);
    if (!parentItem || !childItem) {
      continue;
    }
    parentItem.children = parentItem.children ?? [];
    parentItem.children.push(childItem);
  }

  // Roots = items whose recorded parent isn't in our id map
  const roots: OrganizationTreeItem[] = [];
  for (const [id, item] of itemsById.entries()) {
    const parentId = parentIdById.get(id);
    if (!parentId || !itemsById.has(parentId)) {
      roots.push(item);
    }
  }
  return roots;
};

/**
 * Walks the unfiltered tree and collects every node whose label matches the
 * search term, along with its full ancestor chain. Used for the filtered tree
 * view to identify hits and order them in DFS sequence.
 */
export const collectSearchHits: PureFunction<
  { items: OrganizationTreeItem[]; searchTerm: string },
  SearchHit[]
> = ({ items, searchTerm }) => {
  const term = searchTerm.toLowerCase();
  const results: SearchHit[] = [];

  const walk = (
    nodes: OrganizationTreeItem[],
    ancestors: SearchHitAncestor[]
  ): void => {
    for (const node of nodes) {
      if (node.label.toLowerCase().includes(term)) {
        results.push({ item: node, ancestors: [...ancestors] });
      }
      if (node.children) {
        walk(node.children, [...ancestors, { id: node.id, label: node.label }]);
      }
    }
  };

  walk(items, []);
  return results;
};

const isWordChar = (char: string): boolean => /[\p{L}\p{N}]/u.test(char);

/** `StringFilter` has no case-insensitive equality, so try the casings org names use. */
export const exactNameCasings = (term: string): string[] => {
  const title = term.split(' ').map(capitalize).join(' ');
  return [...new Set([term, term.toLowerCase(), term.toUpperCase(), title])];
};

const matchTier = (label: string, term: string): number => {
  const name = label.toLowerCase();
  if (name === term) {
    return 0;
  }
  if (name.startsWith(term)) {
    return 1;
  }
  for (let i = name.indexOf(term, 1); i !== -1; i = name.indexOf(term, i + 1)) {
    if (!isWordChar(name[i - 1])) {
      return 2;
    }
  }
  return 3;
};

export const isSelectableOrgId = (id: string): boolean =>
  !isUserOrgGroupingNode(id) &&
  !isShowMoreItemId(id) &&
  !isPlaceholderItemId(id);

/**
 * Tree order buries key orgs ("Go") under substring hits ("Django"), so rank
 * by match tier, then repository count, then tree order.
 */
export const findBestSearchHitIndex: PureFunction<
  { hits: SearchHit[]; searchTerm: string },
  number
> = ({ hits, searchTerm }) => {
  const term = searchTerm.toLowerCase();
  let bestIndex = 0;
  let bestTier = Number.POSITIVE_INFINITY;
  let bestRepositoryCount = -1;
  hits.forEach(({ item }, index) => {
    if (!isSelectableOrgId(item.id)) {
      return;
    }
    const tier = matchTier(item.label, term);
    const repositoryCount = item.repositoryCount ?? 0;
    if (
      tier < bestTier ||
      (tier === bestTier && repositoryCount > bestRepositoryCount)
    ) {
      bestIndex = index;
      bestTier = tier;
      bestRepositoryCount = repositoryCount;
    }
  });
  return bestIndex;
};

// ── Pruned tree for filtered tree view ──────────────────────────────────────

type PrunedRowShared = {
  id: string;
  label: string;
  depth: number;
  repositoryCount?: number;
  icon?: LucideIcon;
  isEditable?: boolean;
};

type PrunedFolderShared = PrunedRowShared & {
  type: 'folder';
  /** Spine children render expanded and collapse in place; others are fetched on expand. */
  hasSpineChildren: boolean;
};

export type PrunedFolderRow =
  | (PrunedFolderShared & { kind: 'hit'; matchIndex: number })
  | (PrunedFolderShared & { kind: 'ancestor' })
  | (PrunedFolderShared & { kind: 'revealed' });

export type PrunedMatchRow =
  | (PrunedRowShared & {
      type: 'match';
      kind: 'hit';
      matchIndex: number;
    })
  | (PrunedRowShared & {
      type: 'match';
      kind: 'revealed';
    });

export type PrunedTreeRow =
  | PrunedFolderRow
  | PrunedMatchRow
  | { type: 'more'; folderId: string; count: number; depth: number };

/**
 * Builds a flat array of row descriptors for the filtered search tree view.
 *
 * Given the full tree and the ordered list of search hits, this prunes
 * non-matching branches entirely and collapses non-matching siblings within
 * matching folders behind `"+ N more items"` hints.
 */
export const buildPrunedTree: PureFunction<
  { items: OrganizationTreeItem[]; searchHits: SearchHit[] },
  PrunedTreeRow[]
> = ({ items, searchHits }) => {
  const hitIds = new Set(searchHits.map((h) => h.item.id));
  const hitIndexMap = new Map(searchHits.map((h, i) => [h.item.id, i]));

  // Pre-compute which nodes have hits in their subtree
  const subtreeHasHit = new Map<string, boolean>();
  const markSubtree = (nodes: OrganizationTreeItem[]): boolean => {
    let any = false;
    for (const node of nodes) {
      const selfHit = hitIds.has(node.id);
      const childHit = node.children ? markSubtree(node.children) : false;
      const has = selfHit || childHit;
      subtreeHasHit.set(node.id, has);
      if (has) {
        any = true;
      }
    }
    return any;
  };
  markSubtree(items);

  const rows: PrunedTreeRow[] = [];

  const walk = (nodes: OrganizationTreeItem[], depth: number): void => {
    for (const node of nodes) {
      if (!subtreeHasHit.get(node.id)) {
        continue;
      }

      const isHit = hitIds.has(node.id);
      const hasChildren = (node.children?.length ?? 0) > 0;

      if (hasChildren) {
        if (isHit) {
          const matchIndex = hitIndexMap.get(node.id);
          if (matchIndex != null) {
            rows.push({
              type: 'folder',
              kind: 'hit',
              matchIndex,
              id: node.id,
              label: node.label,
              depth,
              hasSpineChildren: true,
              repositoryCount: node.repositoryCount,
              icon: node.icon,
              isEditable: node.isEditable
            });
          }
        } else {
          rows.push({
            type: 'folder',
            kind: 'ancestor',
            id: node.id,
            label: node.label,
            depth,
            hasSpineChildren: true,
            repositoryCount: node.repositoryCount,
            icon: node.icon,
            isEditable: node.isEditable
          });
        }

        const matchingChildren: OrganizationTreeItem[] = [];
        let nonMatchingLeafCount = 0;

        for (const child of node.children ?? []) {
          if (subtreeHasHit.get(child.id)) {
            matchingChildren.push(child);
          } else {
            nonMatchingLeafCount++;
          }
        }

        walk(matchingChildren, depth + 1);

        // Always emit a `more` row for every spine node (folder or hit). The
        // renderer reads the real hidden count from
        // `hiddenSiblingCountByFolder` (derived from `childCount`) and
        // suppresses the row when count === 0. `count` here is only the
        // count derivable from the input tree itself — for a sparse spine
        // it's typically 0 and `hiddenSiblingCountByFolder` has the real value.
        rows.push({
          type: 'more',
          folderId: node.id,
          count: nonMatchingLeafCount,
          depth: depth + 1
        });
      } else if (isHit) {
        // Hit leaves have no spine descendants. The renderer promotes them
        // to folder rows when `childCount` reports they have children — the
        // `more` affordance is reserved for revealing non-matching siblings
        // under spine folders.
        const matchIndex = hitIndexMap.get(node.id);
        if (matchIndex != null) {
          rows.push({
            type: 'match',
            kind: 'hit',
            matchIndex,
            id: node.id,
            label: node.label,
            depth,
            icon: node.icon,
            repositoryCount: node.repositoryCount,
            isEditable: node.isEditable
          });
        }
      }
    }
  };

  walk(items, 0);
  return rows;
};
